import { pythonGenerator, Order } from 'blockly/python';

/**
 * Chuẩn hóa tên biến tiếng Việt có dấu thành chuẩn Pythonic snake_case không dấu
 */
export function sanitizeVariableName(name) {
  if (!name || typeof name !== 'string') return 'v';
  
  // 1. Chuyển đổi ký tự tiếng Việt có dấu thành không dấu
  let clean = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  clean = clean.replace(/đ/g, "d").replace(/Đ/g, "D");

  // 2. Chuyển sang chữ thường cho chuẩn snake_case
  clean = clean.toLowerCase();

  // 3. Thay thế khoảng trắng và ký tự đặc biệt bằng dấu gạch dưới (_)
  clean = clean.replace(/[^a-z0-9_]/g, "_");

  // 4. Nếu bắt đầu bằng chữ số, thêm tiền tố gạch dưới
  clean = clean.replace(/^(\d)/, "_$1");

  // 5. Thu gọn nhiều dấu gạch dưới liên tiếp và loại bỏ ở hai đầu
  clean = clean.replace(/_+/g, "_").replace(/^_+|_+$/g, "");

  if (!clean) clean = 'v';

  // 6. Tránh từ khóa hệ thống của Python
  const PYTHON_KEYWORDS = new Set([
    'False', 'None', 'True', 'and', 'as', 'assert', 'async', 'await',
    'break', 'class', 'continue', 'def', 'del', 'elif', 'else', 'except',
    'finally', 'for', 'from', 'global', 'if', 'import', 'in', 'is', 'lambda',
    'nonlocal', 'not', 'or', 'pass', 'raise', 'return', 'try', 'while',
    'with', 'yield', 'int', 'float', 'str', 'list', 'dict', 'set', 'robot',
    'time', 'range', 'math', 'urandom'
  ]);

  if (PYTHON_KEYWORDS.has(clean)) {
    clean = `${clean}_var`;
  }

  return clean;
}

/**
 * Quét cây khối con để tìm danh sách các biến toàn cục THỰC SỰ bị thay đổi/gán giá trị trong hàm
 */
function getModifiedGlobalsStmt(startBlock, localArgs = [], workspace) {
  if (!startBlock) return '';
  const descendants = startBlock.getDescendants ? startBlock.getDescendants(false) : [];
  const modifiedVars = new Set();
  const localSet = new Set(localArgs.map(a => sanitizeVariableName(a).toLowerCase()));

  for (const b of descendants) {
    if (b.type === 'variables_set' || b.type === 'math_change') {
      const varId = b.getFieldValue('VAR');
      let varName = varId;
      if (pythonGenerator && pythonGenerator.getVariableName) {
        varName = pythonGenerator.getVariableName(varId);
      } else {
        varName = sanitizeVariableName(varId);
      }
      if (varName && !localSet.has(varName.toLowerCase())) {
        modifiedVars.add(varName);
      }
    }
  }

  if (modifiedVars.size === 0) return '';
  return `    global ${[...modifiedVars].join(', ')}\n`;
}

/**
 * Làm sạch tham số số lần lặp cho range(...)
 */
function cleanRangeTimes(timesStr) {
  const str = String(timesStr).trim();
  if (/^\d+$/.test(str)) {
    return str;
  }
  if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(str)) {
    return str;
  }
  if (/^int\(.+\)$/.test(str)) {
    return str;
  }
  return `int(${str})`;
}

/**
 * Định dạng thân vòng lặp để chống hiện tượng khóa CPU (Busy-Wait Loop)
 * - Nếu thân vòng lặp rỗng hoặc chỉ có pass/khoảng trắng: sinh robot.sleep(0.01) để nhường CPU
 * - Nếu thân vòng lặp có code: chèn thêm robot.sleep(0.005) ở cuối chu kỳ để MicroPython Event Loop xử lý sự kiện
 */
function formatLoopBranch(generator, block, inputName = 'DO') {
  const indent = (generator && generator.INDENT) || '    ';
  let branch = generator.statementToCode(block, inputName);

  if (!branch || !branch.trim() || branch.trim().split('\n').every(l => !l.trim() || l.trim() === 'pass')) {
    return `${indent}robot.sleep(0.01)\n`;
  }

  if (!branch.endsWith('\n')) {
    branch += '\n';
  }

  if (!branch.trim().endsWith('robot.sleep(0.005)')) {
    branch += `${indent}robot.sleep(0.005)\n`;
  }

  return branch;
}


/**
 * ĐĂNG KÝ BỘ BIÊN DỊCH PYTHON CHO KHỐI LỆNH RETOCAR (LEGO SPIKE STYLE)
 * Hỗ trợ sinh mã MicroPython chuẩn 1-1 cho vi điều khiển ESP32 robot RETOCAR-STARTER.
 */
export function registerPythonGenerators() {
  const originalInit = pythonGenerator.init ? pythonGenerator.init.bind(pythonGenerator) : null;
  pythonGenerator.init = function (workspace) {
    this.workspace_ = workspace;
    if (originalInit) {
      originalInit(workspace);
    }
  };

  // Chuẩn hóa bộ lấy tên biến của pythonGenerator
  pythonGenerator.getVariableName = function (nameOrId) {
    if (!nameOrId) return 'v';
    let rawName = typeof nameOrId === 'object' ? (nameOrId.name || nameOrId.id || '') : nameOrId;
    const ws = this.workspace_ || (this.nameDB_ ? (this.nameDB_.variableMap_ || this.nameDB_.variableMap) : null);
    if (ws) {
      const vmap = ws.getVariableMap ? ws.getVariableMap() : (ws.getAllVariables ? ws : null);
      if (vmap && typeof vmap.getVariableById === 'function') {
        const model = vmap.getVariableById(rawName);
        if (model) {
          rawName = model.name;
        }
      }
    }
    return sanitizeVariableName(rawName);
  };

  // ===========================================================================
  // 1. SỰ KIỆN (EVENTS)
  // ===========================================================================
  pythonGenerator.forBlock['when_program_starts'] = function (block, generator) {
    return '';
  };

  pythonGenerator.forBlock['when_color_detected'] = function (block, generator) {
    return '';
  };

  pythonGenerator.forBlock['when_button_event'] = function (block, generator) {
    return '';
  };

  pythonGenerator.forBlock['when_timer_greater'] = function (block, generator) {
    return '';
  };

  pythonGenerator.forBlock['when_condition_true'] = function (block, generator) {
    return '';
  };

  // ===========================================================================
  // 2. ĐIỀU KHIỂN (CONTROL)
  // ===========================================================================
  pythonGenerator.forBlock['wait_seconds'] = function (block, generator) {
    const seconds = generator.valueToCode(block, 'SECONDS', Order.NONE) || block.getFieldValue('SECONDS') || 1;
    return `robot.sleep(${seconds})\n`;
  };

  pythonGenerator.forBlock['repeat_times'] = function (block, generator) {
    const times = generator.valueToCode(block, 'TIMES', Order.NONE) || block.getFieldValue('TIMES') || 10;
    const branch = formatLoopBranch(generator, block, 'DO');
    const cleanTimes = cleanRangeTimes(times);
    return `for _ in range(${cleanTimes}):\n${branch}`;
  };

  pythonGenerator.forBlock['forever_loop'] = function (block, generator) {
    const branch = formatLoopBranch(generator, block, 'DO');
    return `while True:\n${branch}`;
  };

  pythonGenerator.forBlock['if_then'] = function (block, generator) {
    const condition = generator.valueToCode(block, 'CONDITION', Order.NONE) || 'False';
    const branch = generator.statementToCode(block, 'DO') || '    pass\n';
    return `if ${condition}:\n${branch}`;
  };

  pythonGenerator.forBlock['if_then_else'] = function (block, generator) {
    const condition = generator.valueToCode(block, 'CONDITION', Order.NONE) || 'False';
    const branch = generator.statementToCode(block, 'DO') || '    pass\n';
    const elseBlock = block.getInputTargetBlock('ELSE');

    if (elseBlock && (elseBlock.type === 'if_then' || elseBlock.type === 'if_then_else')) {
      const elseCode = generator.blockToCode(elseBlock);
      const elseStr = Array.isArray(elseCode) ? elseCode[0] : (elseCode || '');
      if (elseStr && elseStr.trim().startsWith('if ')) {
        const elifCode = elseStr.trim().replace(/^if\s+/, 'elif ');
        return `if ${condition}:\n${branch}${elifCode}\n`;
      }
    }

    const elseBranch = generator.statementToCode(block, 'ELSE') || '    pass\n';
    return `if ${condition}:\n${branch}else:\n${elseBranch}`;
  };

  pythonGenerator.forBlock['wait_until'] = function (block, generator) {
    const indent = (generator && generator.INDENT) || '    ';
    const cond = generator.valueToCode(block, 'CONDITION', Order.NONE) || 'False';
    return `while not (${cond}):\n${indent}robot.sleep(0.01)\n`;
  };

  pythonGenerator.forBlock['repeat_until'] = function (block, generator) {
    const cond = generator.valueToCode(block, 'CONDITION', Order.NONE) || 'False';
    const branch = formatLoopBranch(generator, block, 'DO');
    return `while not (${cond}):\n${branch}`;
  };

  pythonGenerator.forBlock['controls_repeat_ext'] = pythonGenerator.forBlock['repeat_times'];
  pythonGenerator.forBlock['controls_repeat'] = pythonGenerator.forBlock['repeat_times'];
  pythonGenerator.forBlock['controls_whileUntil'] = function (block, generator) {
    const until = block.getFieldValue('MODE') === 'UNTIL';
    let cond = generator.valueToCode(block, 'BOOL', Order.NONE) || 'False';
    if (until) {
      cond = `not (${cond})`;
    }
    const branch = formatLoopBranch(generator, block, 'DO');
    return `while ${cond}:\n${branch}`;
  };

  pythonGenerator.forBlock['loop_continue'] = function (block, generator) {
    return 'continue\n';
  };

  pythonGenerator.forBlock['loop_break'] = function (block, generator) {
    return 'break\n';
  };

  // ===========================================================================
  // 3. CHUYỂN ĐỘNG & SERVO (MOVEMENT & ACTUATOR)
  // ===========================================================================
  pythonGenerator.forBlock['reto_direction_picker'] = function (block) {
    const dir = block.getFieldValue('DIRECTION') || 'FORWARD';
    return [`"${dir}"`, Order.ATOMIC];
  };

  pythonGenerator.forBlock['reto_motor_side_picker'] = function (block) {
    const motor = block.getFieldValue('MOTOR') || 'LEFT';
    return [`"${motor}"`, Order.ATOMIC];
  };

  pythonGenerator.forBlock['reto_button_picker'] = function (block) {
    const btn = block.getFieldValue('BUTTON') || '1';
    return [btn, Order.ATOMIC];
  };

  pythonGenerator.forBlock['reto_color_picker'] = function (block) {
    let color = block.getFieldValue('COLOR') || 'RED';
    if (color === 'MAGENTA') color = 'PURPLE';
    return [`"${color}"`, Order.ATOMIC];
  };

  pythonGenerator.forBlock['reto_move_direction_speed'] = function (block, generator) {
    const dirField = block.getFieldValue('DIRECTION') || 'FORWARD';
    const direction = block.getInput('DIRECTION') ? (generator.valueToCode(block, 'DIRECTION', Order.NONE) || `"${dirField}"`) : `"${dirField}"`;
    const speed = block.getInput('SPEED') ? (generator.valueToCode(block, 'SPEED', Order.NONE) || block.getFieldValue('SPEED') || 60) : (block.getFieldValue('SPEED') || 60);
    return `robot.move(${direction}, ${speed})\n`;
  };

  pythonGenerator.forBlock['reto_move_stop'] = function (block, generator) {
    return 'robot.stop()\n';
  };

  pythonGenerator.forBlock['reto_motor_single'] = function (block, generator) {
    const motorField = block.getFieldValue('MOTOR') || 'LEFT';
    const motor = block.getInput('MOTOR') ? (generator.valueToCode(block, 'MOTOR', Order.NONE) || `"${motorField}"`) : `"${motorField}"`;
    const dirField = block.getFieldValue('DIRECTION') || 'FORWARD';
    const direction = block.getInput('DIRECTION') ? (generator.valueToCode(block, 'DIRECTION', Order.NONE) || `"${dirField}"`) : `"${dirField}"`;
    const speed = block.getInput('SPEED') ? (generator.valueToCode(block, 'SPEED', Order.NONE) || block.getFieldValue('SPEED') || 50) : (block.getFieldValue('SPEED') || 50);
    return `robot.set_motor(${motor}, ${direction}, ${speed})\n`;
  };

  pythonGenerator.forBlock['reto_set_servo_angle'] = function (block, generator) {
    const angle = generator.valueToCode(block, 'ANGLE', Order.NONE) || block.getFieldValue('ANGLE') || 90;
    return `robot.set_arm_angle(${angle})\n`;
  };

  pythonGenerator.forBlock['reto_set_arm_angle'] = function (block, generator) {
    const angle = generator.valueToCode(block, 'ANGLE', Order.NONE) || block.getFieldValue('ANGLE') || 90;
    return `robot.set_arm_angle(${angle})\n`;
  };

  // ===========================================================================
  // 4. CẢM BIẾN (SENSORS)
  // ===========================================================================
  pythonGenerator.forBlock['reto_distance_less_than'] = function (block, generator) {
    const distance = generator.valueToCode(block, 'DISTANCE', Order.NONE) || block.getFieldValue('DISTANCE') || 15;
    return [`(robot.get_distance() < ${distance})`, Order.RELATIONAL];
  };

  pythonGenerator.forBlock['reto_get_distance'] = function (block, generator) {
    return ['robot.get_distance()', Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['reto_line_detected'] = function (block, generator) {
    const sensor = block.getFieldValue('SENSOR') || '2';
    const lineColor = block.getFieldValue('LINE_COLOR') || 'BLACK';
    if (lineColor === 'WHITE') {
      return [`not (robot.get_line(${sensor}))`, Order.LOGICAL_NOT];
    }
    return [`robot.get_line(${sensor})`, Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['reto_color_is'] = function (block, generator) {
    const color = generator.valueToCode(block, 'COLOR', Order.NONE) || (block.getFieldValue('COLOR') ? `"${block.getFieldValue('COLOR')}"` : '"RED"');
    return [`(robot.get_color_name() == ${color})`, Order.RELATIONAL];
  };

  pythonGenerator.forBlock['reto_detected_color_id'] = function (block, generator) {
    return ['robot.get_color_id()', Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['reto_button_pressed'] = function (block, generator) {
    const button = block.getFieldValue('BUTTON') || (block.getInput('BUTTON') ? generator.valueToCode(block, 'BUTTON', Order.NONE) : null) || '1';
    const state = block.getFieldValue('STATE') || block.getFieldValue('ACTION') || 'PRESSED';
    if (state === 'RELEASED') {
      return [`not (robot.is_button_pressed(${button}))`, Order.LOGICAL_NOT];
    }
    return [`robot.is_button_pressed(${button})`, Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['reto_get_light'] = function (block, generator) {
    return ['robot.get_light_level()', Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['reto_timer'] = function (block, generator) {
    return ['robot.time_now()', Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['reto_time_now'] = pythonGenerator.forBlock['reto_timer'];

  pythonGenerator.forBlock['reto_reset_timer'] = function (block, generator) {
    return 'robot.reset_timer()\n';
  };

  // ===========================================================================
  // 5. PHẢN HỒI & TÍN HIỆU (FEEDBACK)
  // ===========================================================================
  pythonGenerator.forBlock['reto_set_rgb'] = function (block, generator) {
    const color = generator.valueToCode(block, 'COLOR', Order.NONE) || (block.getFieldValue('COLOR') ? `"${block.getFieldValue('COLOR')}"` : '"RED"');
    return `robot.set_rgb_color(${color})\n`;
  };

  // Hàm chuyển đổi độ sáng phần trăm (0 - 100%) sang giá trị 8-bit PWM (0 - 255) có bảo vệ an toàn (clamp)
  function formatPercentToPwmCode(valCode) {
    const trimmed = (valCode || '').trim();
    if (!trimmed) {
      return '0';
    }
    // Nếu là hằng số số học thuần túy (VD: "100", "0", "50", "-5", "120")
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed);
      const clamped = Math.max(0, Math.min(100, num));
      const pwm = Math.round((clamped * 255) / 100);
      return `${pwm}`;
    }
    // Nếu là biến số hoặc biểu thức phép toán: kẹp an toàn 0 - 100 và quy đổi sang 0 - 255
    return `int(round(max(0, min(100, ${trimmed})) * 2.55))`;
  }

  pythonGenerator.forBlock['retocar_set_rgb_percent'] = function (block, generator) {
    const red = generator.valueToCode(block, 'RED', Order.NONE) || '0';
    const green = generator.valueToCode(block, 'GREEN', Order.NONE) || '0';
    const blue = generator.valueToCode(block, 'BLUE', Order.NONE) || '0';

    const rPwm = formatPercentToPwmCode(red);
    const gPwm = formatPercentToPwmCode(green);
    const bPwm = formatPercentToPwmCode(blue);

    return `robot.set_rgb_color(${rPwm}, ${gPwm}, ${bPwm})\n`;
  };
  pythonGenerator.forBlock['reto_set_rgb_percent'] = pythonGenerator.forBlock['retocar_set_rgb_percent'];

  pythonGenerator.forBlock['reto_rgb_off'] = function (block, generator) {
    return 'robot.set_rgb_color("OFF")\n';
  };

  pythonGenerator.forBlock['reto_set_indicator'] = function (block, generator) {
    const side = block.getFieldValue('SIDE') || 'BOTH';
    const state = block.getFieldValue('STATE') || 'ON';
    return `robot.set_indicator("${side}", ${state === 'ON' ? 'True' : 'False'})\n`;
  };

  pythonGenerator.forBlock['reto_buzzer_tone'] = function (block, generator) {
    const freq = generator.valueToCode(block, 'FREQ', Order.NONE) || block.getFieldValue('FREQ') || 1000;
    return `robot.buzzer_tone(${freq})\n`;
  };

  pythonGenerator.forBlock['reto_buzzer_off'] = function (block, generator) {
    return 'robot.buzzer_off()\n';
  };

  // ===========================================================================
  // 6. TOÁN TỬ & PHÉP TÍNH (OPERATORS)
  // ===========================================================================
  pythonGenerator.forBlock['operator_arithmetic'] = function (block, generator) {
    const op = block.getFieldValue('OP') || 'ADD';
    let pyOp = '+';
    let order = Order.ADDITIVE;
    if (op === 'ADD' || op === '+') {
      pyOp = '+';
      order = Order.ADDITIVE;
    } else if (op === 'MINUS' || op === '-') {
      pyOp = '-';
      order = Order.ADDITIVE;
    } else if (op === 'MULTIPLY' || op === '*' || op === '×') {
      pyOp = '*';
      order = Order.MULTIPLICATIVE;
    } else if (op === 'DIVIDE' || op === '/' || op === '÷') {
      pyOp = '/';
      order = Order.MULTIPLICATIVE;
    }
    const a = generator.valueToCode(block, 'A', order) || generator.valueToCode(block, 'NUM1', order) || '0';
    const b = generator.valueToCode(block, 'B', order) || generator.valueToCode(block, 'NUM2', order) || (pyOp === '/' ? '1' : '0');
    return [`(${a} ${pyOp} ${b})`, order];
  };

  pythonGenerator.forBlock['operator_compare'] = function (block, generator) {
    const a = generator.valueToCode(block, 'A', Order.RELATIONAL) || generator.valueToCode(block, 'OPERAND1', Order.RELATIONAL) || '0';
    let op = block.getFieldValue('OP') || '==';
    if (op === '=') op = '==';
    const b = generator.valueToCode(block, 'B', Order.RELATIONAL) || generator.valueToCode(block, 'OPERAND2', Order.RELATIONAL) || '0';
    return [`(${a} ${op} ${b})`, Order.RELATIONAL];
  };

  pythonGenerator.forBlock['operator_logic'] = function (block, generator) {
    const a = generator.valueToCode(block, 'A', Order.LOGICAL_AND) || 'False';
    const op = block.getFieldValue('OP') || 'and';
    const b = generator.valueToCode(block, 'B', Order.LOGICAL_AND) || 'False';
    return [`(${a} ${op} ${b})`, Order.LOGICAL_AND];
  };

  pythonGenerator.forBlock['operator_not'] = function (block, generator) {
    const boolVal = generator.valueToCode(block, 'BOOL', Order.LOGICAL_NOT) || 'False';
    return [`(not ${boolVal})`, Order.LOGICAL_NOT];
  };

  pythonGenerator.forBlock['operator_random'] = function (block, generator) {
    const from = generator.valueToCode(block, 'FROM', Order.NONE) || block.getFieldValue('FROM') || 1;
    const to = generator.valueToCode(block, 'TO', Order.NONE) || block.getFieldValue('TO') || 10;
    return [`urandom.randint(int(${from}), int(${to}))`, Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['operator_between'] = function (block, generator) {
    const val = generator.valueToCode(block, 'VAL', Order.NONE) || '0';
    const min = generator.valueToCode(block, 'MIN', Order.NONE) || '0';
    const max = generator.valueToCode(block, 'MAX', Order.NONE) || '0';
    return [`(${min} <= ${val} <= ${max})`, Order.RELATIONAL];
  };

  pythonGenerator.forBlock['operator_mod'] = function (block, generator) {
    const a = generator.valueToCode(block, 'A', Order.MULTIPLICATIVE) || '0';
    const b = generator.valueToCode(block, 'B', Order.MULTIPLICATIVE) || '1';
    return [`(${a} % ${b})`, Order.MULTIPLICATIVE];
  };

  pythonGenerator.forBlock['operator_round'] = function (block, generator) {
    const num = generator.valueToCode(block, 'NUM', Order.NONE) || '0';
    return [`round(${num})`, Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['operator_math_op'] = function (block, generator) {
    const op = block.getFieldValue('OP') || 'abs';
    const num = generator.valueToCode(block, 'NUM', Order.NONE) || '0';
    switch (op) {
      case 'abs':
        return [`abs(${num})`, Order.FUNCTION_CALL];
      case 'floor':
        return [`math.floor(${num})`, Order.FUNCTION_CALL];
      case 'ceiling':
        return [`math.ceil(${num})`, Order.FUNCTION_CALL];
      case 'sqrt':
        return [`math.sqrt(${num})`, Order.FUNCTION_CALL];
      case 'sin':
        return [`math.sin(math.radians(${num}))`, Order.FUNCTION_CALL];
      case 'cos':
        return [`math.cos(math.radians(${num}))`, Order.FUNCTION_CALL];
      case 'tan':
        return [`math.tan(math.radians(${num}))`, Order.FUNCTION_CALL];
      case 'asin':
        return [`math.degrees(math.asin(${num}))`, Order.FUNCTION_CALL];
      case 'acos':
        return [`math.degrees(math.acos(${num}))`, Order.FUNCTION_CALL];
      case 'atan':
        return [`math.degrees(math.atan(${num}))`, Order.FUNCTION_CALL];
      case 'ln':
        return [`math.log(${num})`, Order.FUNCTION_CALL];
      case 'log':
        return [`math.log10(${num})`, Order.FUNCTION_CALL];
      case 'exp':
        return [`math.exp(${num})`, Order.FUNCTION_CALL];
      case 'pow10':
        return [`pow(10, ${num})`, Order.FUNCTION_CALL];
      default:
        return [`math.${op}(${num})`, Order.FUNCTION_CALL];
    }
  };

  pythonGenerator.forBlock['math_number'] = function (block, generator) {
    const n = block.getFieldValue('NUM') || 0;
    return [String(n), Order.ATOMIC];
  };

  pythonGenerator.forBlock['math_change'] = function (block, generator) {
    const varName = generator.getVariableName(block.getFieldValue('VAR')) || 'count';
    const delta = generator.valueToCode(block, 'DELTA', Order.ADDITIVE) || '0';
    const numDelta = Number(delta);
    if (!isNaN(numDelta)) {
      if (numDelta < 0) {
        return `${varName} -= ${Math.abs(numDelta)}\n`;
      } else {
        return `${varName} += ${numDelta}\n`;
      }
    }
    return `${varName} += ${delta}\n`;
  };

  // ===========================================================================
  // 7. DANH SÁCH DỮ LIỆU (LISTS / ARRAYS)
  // ===========================================================================
  pythonGenerator.forBlock['reto_list_reporter'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('VAR')) || 'my_list';
    return [listName, Order.ATOMIC];
  };

  pythonGenerator.forBlock['reto_list_create_empty'] = function (block, generator) {
    return ['[]', Order.ATOMIC];
  };

  pythonGenerator.forBlock['reto_list_add'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    const item = generator.valueToCode(block, 'ITEM', Order.NONE) || '0';
    return `${listName}.append(${item})\n`;
  };

  pythonGenerator.forBlock['reto_list_delete'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    const idx = generator.valueToCode(block, 'INDEX', Order.NONE) || '1';
    return `${listName}.pop(int(${idx}) - 1)\n`;
  };

  pythonGenerator.forBlock['reto_list_delete_all'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    return `${listName}.clear()\n`;
  };

  pythonGenerator.forBlock['reto_list_insert'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    const idx = generator.valueToCode(block, 'INDEX', Order.NONE) || '1';
    const item = generator.valueToCode(block, 'ITEM', Order.NONE) || '0';
    return `${listName}.insert(int(${idx}) - 1, ${item})\n`;
  };

  pythonGenerator.forBlock['reto_list_replace'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    const idx = generator.valueToCode(block, 'INDEX', Order.NONE) || '1';
    const item = generator.valueToCode(block, 'ITEM', Order.NONE) || '0';
    return `${listName}[int(${idx}) - 1] = ${item}\n`;
  };

  pythonGenerator.forBlock['reto_list_get'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    const idx = generator.valueToCode(block, 'INDEX', Order.NONE) || '1';
    return [`${listName}[int(${idx}) - 1]`, Order.INDEX];
  };

  pythonGenerator.forBlock['reto_list_length'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    return [`len(${listName})`, Order.FUNCTION_CALL];
  };

  pythonGenerator.forBlock['reto_list_contains'] = function (block, generator) {
    const listName = generator.getVariableName(block.getFieldValue('LIST')) || 'my_list';
    const item = generator.valueToCode(block, 'ITEM', Order.NONE) || '0';
    return [`(${item} in ${listName})`, Order.RELATIONAL];
  };

  // ===========================================================================
  // 8. THỦ TỤC / KHỐI CỦA TÔI (PROCEDURES / CUSTOM BLOCKS)
  // ===========================================================================
  pythonGenerator.forBlock['procedures_defnoreturn'] = function (block, generator) {
    const funcName = generator.getVariableName(block.getFieldValue('NAME'));
    const rawArgs = block.arguments_ || block.getVars() || [];
    const args = rawArgs.map(a => generator.getVariableName(a));
    const branch = generator.statementToCode(block, 'STACK') || '    pass\n';
    const modifiedGlobals = getModifiedGlobalsStmt(block.getInputTargetBlock('STACK'), args, generator.workspace_);
    const code = `def ${funcName}(${args.join(', ')}):\n${modifiedGlobals}${branch}`;
    generator.definitions_['%' + funcName] = code;
    return null;
  };

  pythonGenerator.forBlock['procedures_defreturn'] = function (block, generator) {
    const funcName = generator.getVariableName(block.getFieldValue('NAME'));
    const rawArgs = block.arguments_ || block.getVars() || [];
    const args = rawArgs.map(a => generator.getVariableName(a));
    const branch = generator.statementToCode(block, 'STACK') || '';
    const returnVal = generator.valueToCode(block, 'RETURN', Order.NONE) || 'None';
    const modifiedGlobals = getModifiedGlobalsStmt(block.getInputTargetBlock('STACK'), args, generator.workspace_);
    const code = `def ${funcName}(${args.join(', ')}):\n${modifiedGlobals}${branch}    return ${returnVal}\n`;
    generator.definitions_['%' + funcName] = code;
    return null;
  };
}

/**
 * BIÊN DỊCH WORKSPACE THÀNH MÃ MICROPYTHON HOÀN CHỈNH CHO RETOCAR ESP32
 * Tuân thủ chuẩn PEP 8: Định nghĩa hàm tùy chỉnh (def ...) và biến số ở cấp cao nhất (top-level),
 * chỉ thụt lề luồng chạy kịch bản chính bên trong khối try: ... except KeyboardInterrupt:.
 */
export function generateRetoCarPythonCode(workspace) {
  if (!workspace) return '';

  pythonGenerator.workspace_ = workspace;
  pythonGenerator.INDENT = '    ';
  pythonGenerator.init(workspace);

  const topBlocks = workspace.getTopBlocks(true);
  const mainBodies = [];
  const eventEntries = [];

  const START_TYPES = ['when_program_starts'];
  const EVENT_TYPES = [
    'when_color_detected',
    'when_button_event',
    'when_timer_greater',
    'when_condition_true'
  ];
  const PROCEDURE_TYPES = [
    'procedures_defnoreturn',
    'procedures_defreturn'
  ];
  const WHITELIST_ENTRY_POINTS = [
    ...START_TYPES,
    ...EVENT_TYPES,
    ...PROCEDURE_TYPES
  ];

  const hasEvents = topBlocks.some(b => EVENT_TYPES.includes(b.type));
  pythonGenerator._hasEvents = hasEvents;

  let eventIdx = 0;
  for (const block of topBlocks) {
    // PHÂN LOẠI NGHIÊM NGẶT: BỎ QUA HOÀN TOÀN KHỐI MỒ CÔI (ORPHAN BLOCKS)
    // Nếu khối đỉnh không thuộc danh sách Whitelist Entry-Points, tuyệt đối không sinh mã
    if (!WHITELIST_ENTRY_POINTS.includes(block.type)) {
      continue;
    }

    if (block.type === 'when_program_starts') {
      const nextBlock = block.getNextBlock();
      if (nextBlock) {
        let code = pythonGenerator.blockToCode(nextBlock);
        if (Array.isArray(code)) code = code[0];
        if (code) mainBodies.push(code);
      }
    } else if (EVENT_TYPES.includes(block.type)) {
      eventIdx++;
      const nextBlock = block.getNextBlock();
      let handlerBody = '';
      if (nextBlock) {
        let code = pythonGenerator.blockToCode(nextBlock);
        if (Array.isArray(code)) code = code[0];
        handlerBody = code || '';
      }
      if (!handlerBody.trim()) {
        handlerBody = 'pass\n';
      }
      eventEntries.push({
        idx: eventIdx,
        type: block.type,
        block,
        handlerBody
      });
    } else if (PROCEDURE_TYPES.includes(block.type)) {
      // Định nghĩa thủ tục người dùng tự tạo vào generator.definitions_
      pythonGenerator.blockToCode(block);
    }
  }

  // Khởi tạo các biến số toàn cục ở top-level
  const getVarsFn = workspace.getAllVariables
    ? workspace.getAllVariables.bind(workspace)
    : (workspace.getVariableMap ? () => workspace.getVariableMap().getAllVariables() : null);

  const listInitsMap = workspace.listInitialValues || {};
  const varInits = [];

  if (getVarsFn) {
    const allVars = getVarsFn() || [];
    if (allVars.length > 0) {
      const seenVars = new Set();
      allVars.forEach(v => {
        const rawName = typeof v === 'string' ? v : (v.name || (v.getName ? v.getName() : ''));
        const pyName = pythonGenerator.getVariableName ? pythonGenerator.getVariableName(rawName) : sanitizeVariableName(rawName);
        if (!pyName || seenVars.has(pyName)) return;
        seenVars.add(pyName);

        const varType = (typeof v === 'object' && v.type !== undefined) ? v.type : (typeof v === 'object' && v.getType ? v.getType() : '');
        if (varType === 'list') {
          const initVals = listInitsMap[rawName] || listInitsMap[pyName] || [];
          if (Array.isArray(initVals) && initVals.length > 0) {
            const formattedVals = initVals.map(val => {
              if (typeof val === 'number') return val;
              if (typeof val === 'boolean') return val ? 'True' : 'False';
              const trimmed = String(val).trim();
              if (trimmed === 'True' || trimmed === 'False') return trimmed;
              if (!isNaN(trimmed) && trimmed !== '') return trimmed;
              return JSON.stringify(trimmed);
            });
            varInits.push(`${pyName} = [${formattedVals.join(', ')}]`);
          } else {
            varInits.push(`${pyName} = []`);
          }
        } else {
          varInits.push(`${pyName} = 0`);
        }
      });
    }
  }

  // Xây dựng các hàm xử lý sự kiện dạng Decorator High-Level API với global thông minh
  const eventHandlers = [];

  for (const entry of eventEntries) {
    const idx = entry.idx;
    const b = entry.block;
    const modifiedGlobals = getModifiedGlobalsStmt(b.getNextBlock(), [], workspace);
    const bodyIndented = entry.handlerBody.split('\n').map(l => l ? `    ${l}` : '').join('\n');

    if (entry.type === 'when_color_detected') {
      const colorField = b.getFieldValue('COLOR') || 'RED';
      const color = b.getInput('COLOR') ? (pythonGenerator.valueToCode(b, 'COLOR', Order.NONE) || `"${colorField}"`) : `"${colorField}"`;
      const fnName = `on_color_detected_${idx}`;

      eventHandlers.push(
`@robot.on_color(${color})
def ${fnName}():
${modifiedGlobals}${bodyIndented}`
      );

    } else if (entry.type === 'when_button_event') {
      const button = b.getFieldValue('BUTTON') || '1';
      const action = b.getFieldValue('ACTION') || 'PRESSED';
      const fnName = `on_button_${button}_${action.toLowerCase()}_${idx}`;

      eventHandlers.push(
`@robot.on_button(${button}, "${action}")
def ${fnName}():
${modifiedGlobals}${bodyIndented}`
      );

    } else if (entry.type === 'when_timer_greater') {
      const val = pythonGenerator.valueToCode(b, 'VALUE', Order.NONE) || '0';
      const fnName = `on_timer_greater_${idx}`;

      eventHandlers.push(
`@robot.on_timer(${val})
def ${fnName}():
${modifiedGlobals}${bodyIndented}`
      );

    } else if (entry.type === 'when_condition_true') {
      const cond = pythonGenerator.valueToCode(b, 'CONDITION', Order.NONE) || 'False';
      const fnName = `on_condition_true_${idx}`;

      eventHandlers.push(
`@robot.on_condition(lambda: ${cond})
def ${fnName}():
${modifiedGlobals}${bodyIndented}`
      );
    }
  }

  const customDefs = [];
  delete pythonGenerator.definitions_['from_numbers_import_Number'];
  delete pythonGenerator.definitions_['import_numbers'];

  for (const name in pythonGenerator.definitions_) {
    const defStr = pythonGenerator.definitions_[name];
    if (!defStr || defStr.includes('from numbers import') || defStr.includes('import numbers')) {
      continue;
    }
    if (name.startsWith('%') || defStr.includes('def ')) {
      customDefs.push(defStr.trim());
    }
  }

  pythonGenerator.isInitialized = false;
  if (pythonGenerator.nameDB_) {
    pythonGenerator.nameDB_.reset();
  }

  const varSection = varInits.length > 0 ? varInits.join('\n') + '\n\n' : '';
  const funcSection = (eventHandlers.length > 0 || customDefs.length > 0)
    ? [...eventHandlers, ...customDefs].join('\n\n') + '\n\n'
    : '';

  const mainScript = mainBodies.join('\n').trim();
  let executionBody = mainScript;

  if (eventEntries.length > 0 && !mainScript.includes('while True:')) {
    if (executionBody) {
      executionBody += '\n\nwhile True:\n    robot.sleep(0.01)';
    } else {
      executionBody = 'while True:\n    robot.sleep(0.01)';
    }
  }

  if (!executionBody) {
    executionBody = 'pass';
  }

  const indentedBody = executionBody.split('\n').map(l => l ? `    ${l}` : '').join('\n');

  return `# ==============================================================================
# RETOLOGIX STUDIO - MICROPYTHON GENERATED SCRIPT
# Target Kit: RETOCAR-STARTER (ESP32)
# ==============================================================================
from retologix import RetoCar
import time

robot = RetoCar()
robot.reset_timer()

${varSection}${funcSection}try:
${indentedBody}

except KeyboardInterrupt:
    robot.stop()
    robot.buzzer_off()
`;
}

export const customWorkspaceToCode = generateRetoCarPythonCode;
pythonGenerator.workspaceToCode = function (workspace) {
  return generateRetoCarPythonCode(workspace);
};

